import styled from 'styled-components'
import { colors, media } from 'shared/lib'
import { RaffleDetail } from 'shared/types/raffles'
import { ProductDataItem } from 'shared/types/ConsumerSearch'
import { t } from 'localization'

const TP = 'shared.components.Raffles.RaffleEntryConfirmation'

type RaffleEntryConfirmationProps = {
  raffle: RaffleDetail
  selectedSize?: string
  selectedStore?: string
  trendingProducts: ProductDataItem[]
  onClose: () => void
}

export const RaffleEntryConfirmation = ({
  raffle,
  selectedSize,
  selectedStore,
  trendingProducts,
  onClose,
}: RaffleEntryConfirmationProps) => {
  // Only show first 4 products
  const productsToShow = trendingProducts.slice(0, 4)
  return (
    <Wrapper data-qa="raffle-entry-confirmation">
      <NavBar>
        <NavTitle />
        <CloseButton onClick={onClose} data-qa="raffle-confirmation-close">
          ✕
        </CloseButton>
      </NavBar>
      <Content>
        <HeaderSection>
          <EntrySentLabel>
            {t(`${TP}.raffleEntered`, 'ENTRY SUBMITTED')}
          </EntrySentLabel>
          <Notice>
            {t(
              `${TP}.emailNotice`,
              'CHECK YOUR EMAIL FOR UPDATES AND ALERTS',
            )}
          </Notice>
        </HeaderSection>

        <ProductCard>
          <ProductInfo>
            <ProductImage src={raffle.mainImage.src} alt={raffle.productName} />
            <ProductDetails>
              <ProductName>{raffle.productName}</ProductName>
              <ProductPrice>
                {t(`${TP}.rafflePrice`, 'Price:')} ${raffle.rafflePrice}
              </ProductPrice>
            </ProductDetails>
          </ProductInfo>

          <Divider />

          <EntryDetails>
            {selectedSize && (
              <DetailColumn>
                <DetailLabel>{t(`${TP}.size`, 'Size')}</DetailLabel>
                <DetailValue>{selectedSize}</DetailValue>
              </DetailColumn>
            )}
            {selectedStore && (
              <DetailColumn>
                <DetailLabel>
                  {t(`${TP}.flightClubStore`, 'Pickup Location')}
                </DetailLabel>
                <DetailValue>{selectedStore}</DetailValue>
              </DetailColumn>
            )}
          </EntryDetails>
        </ProductCard>

        {productsToShow.length > 0 && (
          <RecommendedSection>
            <RecommendedHeading>
              {t(`${TP}.recommendedForYou`, 'RECOMMENDED FOR YOU')}
            </RecommendedHeading>
            <ProductGrid>
              {productsToShow.map((product) => (
                <ProductGridItem
                  key={product.slug}
                  href={`/${product.slug}`}
                  data-qa="recommended-product"
                >
                  <ProductImageWrapper>
                    <ProductGridImage src={product.pictureUrl} alt={product.name} />
                  </ProductImageWrapper>
                  <ProductTextInfo>
                    <ProductBrand>{product.brandName}</ProductBrand>
                    <ProductGridName>{product.name}</ProductGridName>
                    <ProductGridPrice>
                      {product.price.localizedValue !== null
                        ? `$${product.price.localizedValue}`
                        : ''}
                    </ProductGridPrice>
                  </ProductTextInfo>
                </ProductGridItem>
              ))}
            </ProductGrid>
          </RecommendedSection>
        )}
      </Content>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  height: 19px;
  flex-shrink: 0;
`

const NavTitle = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
  opacity: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: ${colors.FC2_BLACK};
  padding: 0;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 32px 24px 16px;
  width: 100%;
`

const EntrySentLabel = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_RED};
  margin: 0;
  text-align: center;
  text-transform: uppercase;
`

const Notice = styled.h2`
  font-family: 'Ivar Headline', Georgia, serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1;
  letter-spacing: -0.2px;
  text-transform: uppercase;
  color: ${colors.FC2_RED};
  margin: 0;
  text-align: center;
  max-width: 244px;
`

const ProductCard = styled.div`
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 440px;
  margin: 40px 24px;
  width: calc(100% - 48px);

  ${media.small`
    max-width: 392px;
  `}
`

const ProductInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

const ProductImage = styled.img`
  width: 58px;
  object-fit: cover;
  flex-shrink: 0;
`

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const ProductName = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.FC2_BLACK};
`

const ProductPrice = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
  text-align: left;
`

const Divider = styled.div`
  height: 1px;
  background-color: ${colors.FC2_LIGHTEST_GREY};
  width: 100%;
`

const EntryDetails = styled.div`
  display: flex;
  gap: 32px;
  white-space: nowrap;
`

const DetailColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DetailLabel = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
  margin: 0;
  text-align: left;
`

const DetailValue = styled.p`
  font-family: 'Ivar Text', Georgia, serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  letter-spacing: -0.14px;
  color: ${colors.FC2_BLACK};
  margin: 0;
  text-align: left;
`

const RecommendedSection = styled.div`
  padding: 16px 24px 32px;
  width: 100%;
  margin-top: auto;
`

const RecommendedHeading = styled.h3`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${colors.FC2_BLACK};
  margin: 0 0 16px;
`

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  width: 100%;
`

const ProductGridItem = styled.a`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const ProductImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 3 / 2;
  overflow: hidden;
`

const ProductGridImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform: scale(1.14);
  transform-origin: bottom center;
`

const ProductTextInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ProductBrand = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  color: ${colors.FC2_BLACK};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProductGridName = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.FC2_BLACK};
  margin: 0;
  text-transform: uppercase;
  height: 31px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const ProductGridPrice = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.FC2_BLACK};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
